import { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import VersionStamp from './components/VersionStamp';
import { useAuth } from './firebase/auth-context';
import Login from './pages/Login';
import NotAuthorized from './pages/NotAuthorized';
import Layout from './components/Layout';

// Route-level code-splitting (LA-837): every authenticated page is a
// lazy chunk so the login page no longer pulls Recharts/details pages
// into the initial 900+ kB bundle. Login/NotAuthorized stay eager —
// they are the first paint. The Suspense boundary lives around
// Layout's <Outlet/> so the sidebar stays mounted while a page chunk
// loads.
const Overview = lazy(() => import('./pages/Overview'));
const Activities = lazy(() => import('./pages/Activities'));
const ActivityViewer = lazy(() => import('./pages/ActivityViewer'));
const ReportsList = lazy(() => import('./pages/ReportsList'));
const ReportDetail = lazy(() => import('./pages/ReportDetail'));
const EditsList = lazy(() => import('./pages/EditsList'));
const EditDetail = lazy(() => import('./pages/EditDetail'));
const ChangesList = lazy(() => import('./pages/ChangesList'));
const ChangeDetail = lazy(() => import('./pages/ChangeDetail'));
const Users = lazy(() => import('./pages/Users'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Exports = lazy(() => import('./pages/Exports'));
const Costs = lazy(() => import('./pages/Costs'));

/** Loading spinner shown while auth state resolves. */
function LoadingScreen() {
  return (
    <div className="loading-screen">
      <div className="spinner" aria-label="Loading" />
    </div>
  );
}

/**
 * Protected route — requires an authenticated admin user.
 * Redirects to /login if unauthenticated, /not-authorized if not admin.
 */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/not-authorized" replace />;
  return <>{children}</>;
}

export default function App() {
  const { user, isAdmin, loading } = useAuth();

  return (
    <>
      <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/not-authorized" element={<NotAuthorized />} />
      <Route
        path="/"
        element={
          <AdminRoute>
            <Layout />
          </AdminRoute>
        }
      >
        <Route index element={<Overview />} />
        <Route path="activities" element={<Activities />} />
        <Route path="activities/:uid/:docId" element={<ActivityViewer />} />
        <Route path="changes" element={<ChangesList />} />
        <Route path="changes/:changeId" element={<ChangeDetail />} />
        <Route path="reports" element={<ReportsList />} />
        <Route path="reports/:reportId" element={<ReportDetail />} />
        <Route path="edits" element={<EditsList />} />
        <Route path="edits/:editId" element={<EditDetail />} />
        <Route path="users" element={<Users />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="exports" element={<Exports />} />
        <Route path="costs" element={<Costs />} />
      </Route>
      <Route
        path="*"
        element={
          loading ? (
            <LoadingScreen />
          ) : user && isAdmin ? (
            <Navigate to="/" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      </Routes>
      {/* CA-211: the build-version stamp rides every surface —
          login, layout, every admin pane — the same app-root mount
          every Black Ram Labs web app carries. */}
      <VersionStamp />
    </>
  );
}
